/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { Brand } from './brand';

/**
 * WHERE EACH BRAND EMPHASISES `Sale` — and it is INVERTED between them.
 *
 * ── THE MEASUREMENT ───────────────────────────────────────────────────────
 *
 * Read from the width-pinned decompilations, 2026-08-19/20. Every cell below
 * was re-derived from the capture markup, not from a summary:
 *
 *                     | desktop category nav      | mobile drawer
 *   ------------------|---------------------------|--------------------------
 *   BG                | rgb(176,0,0) — the ONLY   | BLACK — markup is
 *                     | coloured link of 13       | byte-identical to siblings
 *   NM                | rgb(46,46,46) — same as   | rgb(176,0,0) + font-bold
 *                     | all 12 siblings, NOT red  |
 *
 * BG desktop — `.reference/captures/bg-plp-d-1280/App.tsx`, and identically in
 * `bg-search-d-1280` and `bg-home-d-1280`. All 13 anchors in the row carry
 * `box-content block relative text-center uppercase h-[14px] … px-[15.444px]`;
 * only `/c/sale-cat205700` adds `text-[rgb(176,_0,_0)]`. The other twelve
 * (whats-new, shop-by, designers-a-z, womens-clothing, shoes, handbags,
 * accessories, jewelry, beauty, men, kids, home) carry NO colour class at all.
 *
 * NM desktop — `.reference/captures/nm-plp-d-1280/App.tsx`. Every anchor
 * including `/c/sale-cat980731` carries `text-[rgb(46,_46,_46)] text-[18px]
 * tracking-[0.5px] leading-[20px]`. `Sale` is byte-identical to its neighbour
 * `Gifts`. Confirmed unchanged at 768 / 1024 / 1280 / 1440 / 1920: the count of
 * `rgb(46,_46,_46)` is 28 raw (= 14 real; captures emit the page twice) in
 * every one of those directories, and `rgb(176,_0,_0)` never appears on the
 * nav row.
 *
 * BG drawer — `.reference/captures/bg-drawer-375/App.tsx`. `Sale` renders
 * `<h6 className="text-left uppercase text-[16px] leading-[16px]">`, which is
 * byte-identical to `Home` two rows above it. The whole capture contains ZERO
 * `text-[rgb(` occurrences. POSITIVE CONTROL for that negative: the sibling
 * capture `bg-drawer-clothing-375` DOES emit four of them, so the decompiler
 * carries colour on this surface and the absence is evidence, not a gap.
 *
 * NM drawer — `.reference/captures/drawer-open-old-375/App.tsx`. `Sale` is
 * `<div role="button" className="font-bold size-full text-left
 * text-[rgb(176,_0,_0)]">`, where its neighbours `Home` and `Gifts` are the
 * bare `size-full text-left`. `drawer-clothing-375` shows the same treatment
 * one level down on `Last Call`, which is why {@link NavSaleEmphasis.drawer}
 * governs drawer leaf rows too and not just the top level.
 *
 * ── WHY THIS IS A MAP AND NOT TWO TOKENS ──────────────────────────────────
 *
 * The two brands do not render one nav with different numbers. They emphasise
 * `Sale` on DIFFERENT SURFACES, and there is no value — in CSS or in the seed
 * — that expresses "red here, not there, and the other way round next door".
 * A per-silo `emphasis: 'sale'` data flag consumed identically by both
 * surfaces cannot express it either: that was tried on 2026-08-20, produced
 * zero fixes and one regression (BG's drawer `Sale` went red, where live is
 * black), and was reverted. The flag is a fact about the SILO; which surface
 * acts on it is a fact about the BRAND, and they are different facts.
 *
 * ── WHY ONE MAP AND NOT TWO ───────────────────────────────────────────────
 *
 * Both halves could have been bolted onto existing rows — `HEADER_COMPOSITION`
 * for the category nav, `DRAWER_COMPOSITION` (mobile.tsx) for the drawer. They
 * are deliberately NOT, because the whole finding is that the two halves are
 * inverted, and a reader who cannot see both cells at once cannot see that.
 * Split across two files, "BG's drawer isn't red" reads as an oversight and
 * the next agent helpfully fixes it. Side by side, it reads as the measurement
 * it is. This is `header-composition.ts`'s own "WHY ONE MAP AND NOT TWO"
 * argument — gates that have to agree live in one row — applied to a pair that
 * has to DISAGREE.
 *
 * ── `null` MEANS MEASURED-AND-ABSENT ──────────────────────────────────────
 *
 * Both fields are REQUIRED on both brands. A brand that does not emphasise on
 * a surface says so with an explicit `null`, never by omitting the key,
 * because omission cannot be told apart from "nobody has measured this brand
 * yet". Both `null`s here are measurements with citations above, not defaults.
 *
 * A third brand is a COMPILE ERROR rather than a silent inheritance of NM's
 * inversion — the same guarantee `footer-composition.ts` and
 * `header-composition.ts` provide.
 */
export type NavSaleTreatment = {
    /**
     * Live NM's drawer row is `font-bold`; live BG's category-nav link is not
     * bolded — it is distinguished by colour alone, at the same weight as the
     * twelve links beside it. Carried here rather than baked into the CSS rule
     * because it is a per-cell measurement, and the two cells disagree.
     */
    readonly bold: boolean;
};

export type NavSaleEmphasis = {
    /** Desktop category-nav row. BG emphasises here; NM does not. */
    readonly categoryNav: NavSaleTreatment | null;
    /** Mobile drawer, top-level silo rows AND drill/leaf rows within a panel. */
    readonly drawer: NavSaleTreatment | null;
};

/**
 * Sources for all four arms below, re-derived from the capture markup on
 * 2026-08-20 rather than carried over from a summary:
 * `.reference/captures/nm-plp-d-1280/App.tsx` (NM cat nav, cross-checked at
 * search-d-768 / -1024 / -1280 / -1440 / -1920),
 * `.reference/captures/drawer-open-old-375/App.tsx` (NM drawer),
 * `.reference/captures/bg-plp-d-1280/App.tsx` (BG cat nav, identical in
 * bg-search-d-1280 and bg-home-d-1280), and
 * `.reference/captures/bg-drawer-375/App.tsx` (BG drawer, with
 * bg-drawer-clothing-375 as the positive control for its colour-free markup).
 */
export const NAV_SALE_EMPHASIS: Record<Brand, NavSaleEmphasis> = {
    nm: {
        // nm-plp-d-1280: Sale is text-[rgb(46,_46,_46)], identical to Gifts and
        // to the other eleven. Measured absent, not unmeasured.
        categoryNav: null,
        // drawer-open-old-375: font-bold text-[rgb(176,_0,_0)] against bare
        // siblings.
        drawer: { bold: true },
    },
    bg: {
        // bg-plp-d-1280: the only one of 13 with a colour class, and no
        // font-weight class anywhere on the row.
        categoryNav: { bold: false },
        // bg-drawer-375: byte-identical to Home; zero text-[rgb( in the whole
        // capture, with bg-drawer-clothing-375 as the positive control.
        drawer: null,
    },
};

/**
 * The value for the `data-sale-emphasis` attribute, or `undefined` to omit it.
 *
 * Returns a value only when the silo/link IS a sale row (`emphasis === 'sale'`,
 * the seed's own semantic flag) AND this brand emphasises on this surface.
 * Omitting the attribute — rather than emitting a falsy one — is what lets the
 * CSS rule be a bare attribute selector with no brand knowledge in it.
 *
 * The returned token doubles as the weight switch (`bold` vs `plain`) so that
 * one attribute carries the whole treatment and the stylesheet needs no second
 * hook.
 */
export function navSaleEmphasisAttr(
    treatment: NavSaleTreatment | null,
    emphasis: string | undefined
): 'bold' | 'plain' | undefined {
    if (emphasis !== 'sale' || treatment === null) return undefined;
    return treatment.bold ? 'bold' : 'plain';
}
